import 'Agent/Policies/components/PolicyImagesCard.sass';
import React from 'react';
import {useSelector} from 'react-redux';
import {ReducerState} from 'rootReducer';
import {Grid, useMediaQuery} from '@material-ui/core';
import FileDownloadButton from 'Shared/components/FileDownloadButton';
import {renderAsyncView} from 'Shared/components/RenderAsync';
import {addFilterParameters} from 'Shared/utilities/addFilterParameters';
import {toFormattedDateAsUTC} from 'Shared/utilities/DateFormatter';
import {AgentAgencyClaim} from 'Users/models/AgentAgencyClaim';
import {makeStyles} from '@material-ui/core/styles';
import {CardFieldV2} from 'Shared/components/CardFieldV2';
import {AllstateCardV2} from 'Shared/components/AllstateCardV2';
import {GetAppSharp} from '@material-ui/icons';
import {fromAsync} from 'Core/models/Async';
import { themeColors } from 'Theme';

const NoResultsAvailableMessage = () => <div className="policy-images__no-results-available">No Results Available</div>;

const usePolicyImageStyles = makeStyles(theme => ({
  downloadFileBtn: {
    borderRadius: '6.25rem',
    width: '11rem',
    padding: '0.65rem',
    backgroundColor: themeColors.brightBlue_100,
    boxShadow: '0px 3px 1px -2px rgba(0,0,0,0.2), 0px 2px 2px 0px rgba(0,0,0,0.14), 0px 1px 5px 0px rgba(0,0,0,0.12)',
    color: themeColors.blue_600,
    fontWeight: 600,
    '&:hover': {
      backgroundColor: themeColors.blue_400,
      color: themeColors.primaryWhite,
    },
  },
  header: {
    alignItems: 'center',
    [theme.breakpoints.down('xs')]: {
      display: 'none',
    },
    [theme.breakpoints.down('sm')]: {
      paddingRight: '1rem',
    },
  },
  container: {
    margin: '0',
    width: '100%',
  },
  innerGrid: {
    alignItems: 'center',
    borderBottom: '1.5px solid #ccc',
    '&:last-of-type': {
      borderBottom: 'none',
    },
    [theme.breakpoints.down('xs')]: {
      flexDirection: 'column',
      alignItems: 'flex-start',
      padding: '.5rem',
    },
    [theme.breakpoints.down('sm')]: {
      paddingRight: '1rem',
    },
  },
  cardFieldWithoutLabel: {
    display: 'none',
  },
  downloadBtnContainer: {
    [theme.breakpoints.down('xs')]: {
      margin: '-0.25rem',
    },
  },
}));

export const getParams = (encryptedIdentifier: string, eisFlag: boolean) => {
  return eisFlag
    ? addFilterParameters({encryptedIdentifier}).replace('?', '&')
    : addFilterParameters({encryptedIdentifier});
};

const PolicyImagesCard = (props: {selectedAgency: AgentAgencyClaim}) => {
  const {policyImages, policy} = useSelector((state: ReducerState) => state.policy);
  const styles = usePolicyImageStyles();
  const isMobile = useMediaQuery((theme: any) => theme.breakpoints.down('xs'));

  return (
    <AllstateCardV2 title="Policy Images">
      <div className="policy-images__container">
        {renderAsyncView(policyImages, imagesPayload =>
          imagesPayload.images && imagesPayload.images.length > 0 ? (
            <Grid container spacing={2} className={styles.container}>
              <Grid container spacing={4} className={`${styles.header} ${styles.container}`}>
                <Grid item xs={4}>
                  File Name
                </Grid>
                <Grid item xs={4}>
                  Date Created
                </Grid>
                <Grid item xs={4} />
              </Grid>
              {imagesPayload.images.map(image => (
                <Grid container spacing={isMobile ? 2 : 4} className={`${styles.innerGrid} ${styles.container}`}>
                  <Grid item sm={4} md={4}>
                    <CardFieldV2 name={isMobile ? 'File Name' : ''} value={image.description} />
                  </Grid>
                  <Grid item sm={4} md={4}>
                    <CardFieldV2
                      name={isMobile ? 'Date Created' : ''}
                      value={toFormattedDateAsUTC(image.createDate)}
                      styles={!isMobile ? {name: styles.cardFieldWithoutLabel} : undefined}
                    />
                  </Grid>
                  <Grid item sm={4} md={4} className={styles.downloadBtnContainer}>
                    <FileDownloadButton
                      downloadedFileName={image.description ?? ''}
                      flag={true}
                      extension={Array.isArray(image.links) ? '.' + image.links[0]?.rel : '.pdf'}
                      displayTooltip={false}
                      relativeFileLocation={
                        Array.isArray(image.links)
                          ? image.links[0]?.href +
                            getParams(props.selectedAgency.encryptedIdentifier, Boolean(fromAsync(policy).eisFlag))
                          : ''
                      }
                      className={styles.downloadFileBtn}
                      startIcon={<GetAppSharp />}
                    >
                      Download File
                    </FileDownloadButton>
                  </Grid>
                </Grid>
              ))}
            </Grid>
          ) : (
            <NoResultsAvailableMessage />
          )
        )}
      </div>
    </AllstateCardV2>
  );
};

export default PolicyImagesCard;
